import { Form, Input, Skeleton, Space, Typography } from 'antd';

import {
  ConfirmDeletingElement,
  EditPageButtons,
  Error403Unauthorized,
} from '@app/components';
import {
  ERROR,
  ERROR_REQUIRED_FIELD,
  OFF,
  ONE_TIME_CODE,
  PREFIX,
} from '@app/constants';
import { generateSpanWithDataTestId } from '@app/shared/utils';
import { numericValidator } from '@app/utils';

import { useAddLegalEntities } from '@pages/LegalEntities/AddLegalEntities/hooks/useAddLegalEntities';

import type { FC } from 'react';

import '@pages/LegalEntities/AddLegalEntities/styles/AddLegalEntities.desktop.scss';
import '@pages/LegalEntities/AddLegalEntities/styles/AddLegalEntities.tablet.scss';

export const AddLegalEntities: FC = () => {
  const {
    isLoading,
    form,
    submitForm,
    serverError,
    setServerError,
    editableLegalEntitiesId,
    handleCancel,
    handelDeleteLegalEntity,
    hasUserRightForModuleView,
    hasUserRightToCreate,
    hasUserRightToEdit,
    isAddingOrEditingPage,
    modalOpen,
    setModalOpen,
    setInputData,
  } = useAddLegalEntities();

  if (!hasUserRightForModuleView) {
    return <Error403Unauthorized />;
  } else if (isAddingOrEditingPage) {
    const isEditionPage = window.location.href.includes('?id=');

    if (
      (isEditionPage && !hasUserRightToEdit) ||
      (!isEditionPage && !hasUserRightToCreate)
    ) {
      return <Error403Unauthorized />;
    }
  }

  if (isLoading && editableLegalEntitiesId) {
    return <Skeleton active />;
  }

  const whitespaceValidator = (_: unknown, value: string) => {
    if (!value || value.trim().length === 0) {
      return Promise.reject(
        generateSpanWithDataTestId('Поле не может быть пустым', 'alert')
      );
    }
    return Promise.resolve();
  };

  return (
    <div className={`${PREFIX}add-legal-entities`}>
      <Form
        form={form}
        onFinish={submitForm}
        className="form"
        autoComplete={OFF}
      >
        <Space direction="vertical" size="large" className="form-space">
          <div className="container">
            <Typography.Text
              strong
              className="label-require"
              data-testid="name-title"
            >
              Название
            </Typography.Text>

            <Form.Item
              name="Name"
              className="mg-0"
              rules={[
                {
                  required: true,
                  message: generateSpanWithDataTestId(
                    ERROR_REQUIRED_FIELD,
                    'alert'
                  ),
                },
                {
                  validator: whitespaceValidator,
                },
              ]}
            >
              <Input
                data-testid="name-input"
                autoComplete={ONE_TIME_CODE}
                maxLength={200}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Name',
                    isEditPage: Boolean(editableLegalEntitiesId),
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text
              strong
              className="label-require"
              data-testid="tin-title"
            >
              ИНН
            </Typography.Text>

            <Form.Item
              name="TaxPayerId"
              className="mg-0"
              rules={[
                {
                  required: true,
                  message: generateSpanWithDataTestId(
                    ERROR_REQUIRED_FIELD,
                    'alert'
                  ),
                },
                {
                  validator: numericValidator,
                },
              ]}
            >
              <Input
                data-testid="tin-input"
                autoComplete={ONE_TIME_CODE}
                maxLength={12}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'TaxPayerId',
                    isEditPage: Boolean(editableLegalEntitiesId),
                    isTextField: false,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text
              className="label"
              strong
              data-testid="tax-registration-number-title"
            >
              КПП
            </Typography.Text>

            <Form.Item
              name="Kpp"
              className="mg-0"
              rules={[
                {
                  validator: numericValidator,
                },
              ]}
            >
              <Input
                data-testid="tax-registration-number-input"
                autoComplete={ONE_TIME_CODE}
                maxLength={9}
                size="large"
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Kpp',
                    isEditPage: Boolean(editableLegalEntitiesId),
                    isTextField: false,
                  });
                }}
              />
            </Form.Item>
          </div>

          <div className="container">
            <Typography.Text
              className="label"
              strong
              data-testid="comment-title"
            >
              Примечание
            </Typography.Text>

            <Form.Item name="Description" className="mg-0">
              <Input.TextArea
                data-testid="comment-input"
                autoComplete={ONE_TIME_CODE}
                rows={4}
                maxLength={400}
                onChange={(e) => {
                  setInputData({
                    event: e,
                    fieldName: 'Description',
                    isEditPage: Boolean(editableLegalEntitiesId),
                    isTextField: true,
                  });
                }}
              />
            </Form.Item>
          </div>

          <EditPageButtons
            serverError={serverError}
            errorType={ERROR}
            setServerError={setServerError}
            isLoading={isLoading}
            setModalOpen={setModalOpen}
            handleCancel={handleCancel}
            isEditPage={Boolean(editableLegalEntitiesId)}
          />
        </Space>
      </Form>

      <ConfirmDeletingElement
        modalOpen={modalOpen}
        setModalOpen={setModalOpen}
        handleDelete={handelDeleteLegalEntity}
        content="Вы уверены, что хотите удалить юридическое лицо?"
        title="Удаление юридического лица"
        modalDataTestId="delete-legal-entity-confirmation"
        titleDataTestId="delete-legal-entity-confirmation-title"
        descriptionDataTestId="delete-description"
      />
    </div>
  );
};
